import React, { useEffect, useMemo, useState } from 'react';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import config from '../config';
import { fetchWeeklyLeaderboard } from '../services/weeklyLeaderboardService';

const buildAbsoluteUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const base = config.baseApi || '';
  const hasLeadingSlash = url.startsWith('/');
  return `${base}${hasLeadingSlash ? '' : '/'}${url}`;
};

const getInitials = (nameOrUsername) => {
  const raw = (nameOrUsername || '').trim();
  if (!raw) return 'U';

  // Convert underscores/dots to spaces so initials work for usernames like "john_doe".
  const normalized = raw.replace(/[_\.]+/g, ' ');
  const parts = normalized.split(/\s+/).filter(Boolean);
  const picked = parts.slice(0, 2).map((p) => p.charAt(0).toUpperCase());
  return (picked.join('') || raw.charAt(0).toUpperCase()).slice(0, 2);
};

const Avatar = ({ src, name, username }) => {
  const [imgError, setImgError] = useState(false);
  const resolvedSrc = src ? buildAbsoluteUrl(src) : null;
  const initials = getInitials(name || username);

  return (
    <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full overflow-hidden bg-gray-100 border border-[#ECECEC] flex items-center justify-center flex-shrink-0">
      {resolvedSrc && !imgError ? (
        <img
          src={resolvedSrc}
          alt={name || username || 'Profile'}
          className="w-full h-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <span className="text-gray-600 font-semibold">
          {initials}
        </span>
      )}
    </div>
  );
};

const rankIcon = (rank) => {
  const r = Number(rank);
  if (r === 1) return '🥇';
  if (r === 2) return '🥈';
  if (r === 3) return '🥉';
  return null;
};

const rankColors = (rank) => {
  const r = Number(rank);
  if (r === 1 || r === 2 || r === 3) {
    return {
      badge: 'bg-brand text-white border-brand',
      row: 'bg-[#BC6ED7]/10',
    };
  }
  return null;
};

/**
 * Props
 * - groups: Array<{ country: string, group_index: number, users: Array<...> }>
 * - onLoadMoreGroups?: () => Promise<void> | void
 * - hasMoreGroups?: boolean
 * - groupsPageSize?: number
 * - previousWeekGroups?: Array<{ country: string, group_index: number, users: Array<...> }>
 */
const LeaderboardGroupedTable = ({
  groups,
  onLoadMoreGroups,
  hasMoreGroups = false,
  groupsPageSize = 3,
  currentWeekNumber = null,
  previousWeekGroups = [],
}) => {
  const [groupsPage, setGroupsPage] = useState(1);
  const [loadingMoreGroups, setLoadingMoreGroups] = useState(false);
  const [activeGroupKey, setActiveGroupKey] = useState(null);
  const [activeGroup, setActiveGroup] = useState(null);

  const normalizedGroups = useMemo(() => {
    const safeGroups = Array.isArray(groups) ? groups : [];

    return safeGroups.map((group, idx) => {
      const users = Array.isArray(group?.users) ? group.users : [];

      const sortedUsersAll = users
        .slice()
        .sort((a, b) => Number(a?.rank ?? 0) - Number(b?.rank ?? 0));
      const sortedUsersTop = sortedUsersAll.slice(0, 25);

      return {
        country: group?.country || '--',
        group_index: group?.group_index ?? idx + 1,
        users: sortedUsersTop,
        usersAll: sortedUsersAll,
      };
    });
  }, [groups]);

  const hasAnyUsers = normalizedGroups.some((g) => (g.users || []).length > 0);
  const isEmpty = !normalizedGroups.length || !hasAnyUsers;
  const localSliceStart = (groupsPage - 1) * groupsPageSize;
  const localSliceEnd = localSliceStart + groupsPageSize;
  const shouldUseLocalPagination = !onLoadMoreGroups;
  const visibleGroups = shouldUseLocalPagination
    ? normalizedGroups.slice(localSliceStart, localSliceEnd)
    : normalizedGroups;
  const hasMoreLocalGroups = normalizedGroups.length > visibleGroups.length;
  const totalLocalPages = shouldUseLocalPagination
    ? Math.max(1, Math.ceil(normalizedGroups.length / groupsPageSize))
    : 1;
  const canPrev = shouldUseLocalPagination && groupsPage > 1;
  const canNext =
    shouldUseLocalPagination && groupsPage < totalLocalPages;

  const getPaginationRange = () => {
    const total = totalLocalPages;
    const current = groupsPage;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const pages = new Set([1, total, current, current - 1, current + 1]);
    const safePages = Array.from(pages)
      .filter((p) => p >= 1 && p <= total)
      .sort((a, b) => a - b);

    const range = [];
    for (let i = 0; i < safePages.length; i += 1) {
      const p = safePages[i];
      const prev = safePages[i - 1];
      if (i > 0 && p - prev > 1) range.push('dots');
      range.push(p);
    }
    return range;
  };

  const showLoadMore = Boolean(onLoadMoreGroups)
    ? (hasMoreGroups || hasMoreLocalGroups)
    : hasMoreLocalGroups;

  const handleLoadMore = async () => {
    if (loadingMoreGroups) return;

    // If parent supplies a handler, prefer that for lazy loading.
    if (onLoadMoreGroups) {
      const maybePromise = onLoadMoreGroups();
      if (maybePromise && typeof maybePromise.then === 'function') {
        setLoadingMoreGroups(true);
        try {
          await maybePromise;
        } finally {
          setLoadingMoreGroups(false);
        }
      }
      return;
    }

    setGroupsPage((p) => p + 1);
  };

  const handlePrevPage = () => {
    if (!canPrev) return;
    setGroupsPage((p) => Math.max(1, p - 1));
  };

  const handleNextPage = () => {
    if (!canNext) return;
    setGroupsPage((p) => Math.min(totalLocalPages, p + 1));
  };

  // When new data arrives, restart to page 1 so pagination can't get "stuck".
  useEffect(() => {
    setGroupsPage(1);
  }, [groups, groupsPageSize, onLoadMoreGroups]);

  // Keep a stable "active group" snapshot so the modal doesn't flash/close
  // if the parent `groups` prop refreshes and the chosen group disappears briefly.
  useEffect(() => {
    if (!activeGroupKey) {
      setActiveGroup(null);
      return;
    }

    const found = normalizedGroups.find(
      (g) => `${g.country}-${g.group_index}` === activeGroupKey
    );

    // Only update when we can resolve it from current data; otherwise keep the last snapshot.
    if (found) setActiveGroup(found);
  }, [activeGroupKey, normalizedGroups]);

  // Match "modal" behavior: lock background scroll while open.
  useEffect(() => {
    if (!activeGroupKey) return undefined;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [activeGroupKey]);

  const [modalWeekMode, setModalWeekMode] = useState('current'); // 'current' | 'previous'
  const [modalUsersCurrent, setModalUsersCurrent] = useState([]);
  const [modalUsersPrevious, setModalUsersPrevious] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);

  useEffect(() => {
    if (!activeGroup) {
      setModalUsersCurrent([]);
      setModalUsersPrevious(null);
      setModalWeekMode('current');
      setModalError(null);
      return;
    }

    setModalUsersCurrent(Array.isArray(activeGroup.usersAll) ? activeGroup.usersAll : []);
    setModalUsersPrevious(null);
    setModalWeekMode('current');
    setModalError(null);
  }, [activeGroupKey, activeGroup]);

  const canShowPreviousWeek =
    typeof currentWeekNumber === 'number' && currentWeekNumber > 1 && !!activeGroup;

  const handleModalWeekChange = async (mode) => {
    if (!activeGroup) return;

    if (mode === 'current') {
      setModalWeekMode('current');
      setModalError(null);
      return;
    }

    if (!canShowPreviousWeek) return;

    // Prefer using the already-provided previous week data from backend response.
    const prevGroups = Array.isArray(previousWeekGroups) ? previousWeekGroups : [];
    if (prevGroups.length) {
      const match =
        prevGroups.find(
          (g) =>
            g.country === activeGroup.country &&
            Number(g.group_index) === Number(activeGroup.group_index)
        ) || prevGroups[0];

      const usersPrev = Array.isArray(match?.users)
        ? match.users.slice().sort((a, b) => Number(a?.rank ?? 0) - Number(b?.rank ?? 0))
        : [];

      setModalUsersPrevious(usersPrev);
      setModalWeekMode('previous');
      setModalError(null);
      return;
    }

    // If we've already fetched previous week once, just switch.
    if (modalUsersPrevious && Array.isArray(modalUsersPrevious)) {
      setModalWeekMode('previous');
      setModalError(null);
      return;
    }

    try {
      setModalLoading(true);
      setModalError(null);

      const targetWeek = currentWeekNumber - 1;
      const res = await fetchWeeklyLeaderboard({
        weekNumber: targetWeek,
        country: activeGroup.country,
      });

      const groupsPrev = Array.isArray(res?.groups) ? res.groups : [];
      const match =
        groupsPrev.find(
          (g) =>
            g.country === activeGroup.country &&
            Number(g.group_index) === Number(activeGroup.group_index)
        ) || groupsPrev[0];

      const usersPrev = Array.isArray(match?.users)
        ? match.users.slice().sort((a, b) => Number(a?.rank ?? 0) - Number(b?.rank ?? 0))
        : [];

      setModalUsersPrevious(usersPrev);
      setModalWeekMode('previous');
    } catch (e) {
      setModalError(e?.message || 'Failed to load previous week data');
    } finally {
      setModalLoading(false);
    }
  };

  const activeUsers =
    modalWeekMode === 'previous' && modalUsersPrevious
      ? modalUsersPrevious
      : modalUsersCurrent;

  // When there is no data, show only the text (no white card/table container).
  if (isEmpty) {
    return (
      <div className="w-full min-h-[60vh] flex items-center justify-center text-center px-4">
        <div className="text-sm sm:text-base text-gray-500">
          No leaderboard data available yet.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col max-h-[72vh]">
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
        <div className="pt-3">
          <div className="w-full">
            <div className="overflow-x-auto">
              <table className="w-full md:table-fixed table-auto border-collapse min-w-[720px]">
                <colgroup className="hidden md:table-column-group">
                  <col className="w-[14%]" />
                  <col className="w-[24%]" />
                  <col className="w-[48%]" />
                  <col className="w-[14%]" />
                </colgroup>

                <thead className="bg-white border-b border-[#ECECEC]">
                  <tr>
                    <th
                      scope="col"
                      className="px-3 md:px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider"
                    >
                      Rank
                    </th>
                    <th
                      scope="col"
                      className="px-6 md:px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider"
                    >
                      Profile Picture
                    </th>
                    <th
                      scope="col"
                      className="px-8 md:px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider"
                    >
                      Username
                    </th>
                    <th
                      scope="col"
                      className="px-3 md:px-4 py-3 text-center text-sm font-semibold text-gray-500 uppercase tracking-wider"
                    >
                      Points
                    </th>
                    {/* <th
                      scope="col"
                      className="px-3 md:px-4 py-3 text-right text-sm font-semibold text-gray-500 uppercase tracking-wider"
                    >
                      Action
                    </th> */}
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-[#ECECEC]">
                  {visibleGroups.map((group) => {
                    const users = Array.isArray(group.users) ? group.users : [];
                    const usersAll = Array.isArray(group.usersAll)
                      ? group.usersAll
                      : users;
                    const topUser = users.length ? [users[0]] : [];

                    // Main table rows: no special "top 3" background styling.
                    const renderRow = (user) => {
                      const r = Number(user?.rank ?? 0);
                      const isCurrent = Boolean(
                        user?.is_current_user ||
                          user?.isCurrentUser ||
                          user?.is_currentUser
                      );
                      const displayName = user?.name || user?.username || 'N/A';
                      const displayUsername = user?.username
                        ? `@${user.username}`
                        : '';
                      const points = Number(
                        user?.points ?? user?.point ?? user?.score ?? 0
                      );

                      const rowBgClass = isCurrent
                        ? 'bg-[#F2E2F7]/10'
                        : 'bg-white';
                      const rowHoverClass =
                        'hover:bg-white/30 transition-colors';

                      return (
                        <tr
                          key={
                            user?.id ||
                            user?.username ||
                            `${group.country}-${group.group_index}-rank-${r}`
                          }
                          className={`${rowBgClass} ${rowHoverClass}`}
                        >
                          <td className="px-3 md:px-4 py-2.5 align-middle whitespace-nowrap">
                            <span
                              className={`inline-flex items-center justify-center min-w-9 h-7 px-2 rounded-md border text-xs font-semibold ${
                                isCurrent
                                  ? 'border-brand/50 bg-brand/5 text-brand'
                                  : 'border-gray-200 bg-gray-50 text-gray-800'
                              }`}
                            >
                              {r || '--'}
                            </span>
                          </td>

                          <td className="px-6 md:px-4 py-2.5 align-middle whitespace-nowrap">
                            <div className="flex items-center">
                              <Avatar
                                src={
                                  user?.profile_picture ||
                                  user?.profilePicture ||
                                  null
                                }
                                name={user?.name}
                                username={user?.username}
                              />
                            </div>
                          </td>

                          <td className="px-7 md:px-4 py-2.5 align-middle min-w-0">
                            <div className="min-w-0">
                              <div
                                className={`text-sm font-semibold truncate ${
                                  isCurrent ? 'text-brand' : 'text-gray-900'
                                }`}
                                title={displayName}
                              >
                                {displayName}
                              </div>
                              <div
                                className="text-xs text-gray-500 truncate"
                                title={displayUsername || ''}
                              >
                                {displayUsername || '-'}
                              </div>
                              {/* {rankIcon(r) ? (
                                <div className="text-xs text-gray-500">
                                  {rankIcon(r)} Top {r}
                                </div>
                              ) : null} */}
                            </div>
                          </td>

                          <td className="px-3 md:px-4 py-2.5  text-center  align-middle whitespace-nowrap">
                            <span className="text-sm font-semibold text-gray-900 tabular-nums">
                              {Number.isFinite(points)
                                ? points.toLocaleString()
                                : '--'}
                            </span>
                          </td>
                          {/* <td className="px-3 md:px-4 py-2.5 text-right align-middle whitespace-nowrap">
                            <span className="text-xs text-gray-400">—</span>
                          </td> */}
                        </tr>
                      );
                    };

                    return (
                      <React.Fragment
                        key={`${group.country}-${group.group_index}`}
                      >
                        <tr className="bg-white">
                          <td
                            colSpan={5}
                            className="px-3 md:px-4 py-3 border-b border-[#ECECEC]"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="inline-flex items-center gap-2 px-2 py-1 text-xs rounded-full bg-white border border-gray-200 text-gray-700 font-semibold shrink-0">
                                  Group {group.group_index +1}
                                </span>
                                {/* <span className="text-sm font-semibold text-gray-900 truncate">
                                  {group.country}
                                </span> */}
                              </div>
                              <div className="flex items-center gap-3 shrink-0">
                                <span className="text-xs text-gray-500">
                                  {usersAll.length} user
                                  {usersAll.length === 1 ? '' : 's'}
                                </span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setActiveGroupKey(
                                      `${group.country}-${group.group_index}`
                                    )
                                  }
                                  className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-semibold text-white rounded-lg shadow-sm bg-brand hover:opacity-90 transition-colors"
                                >
                                  View all
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                        {topUser.map((u) => renderRow(u))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {onLoadMoreGroups ? (
        showLoadMore ? (
          <div className="px-4 sm:px-6 py-4 border-t border-gray-100 bg-white">
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-gray-500">
                Showing {visibleGroups.length} of {normalizedGroups.length} group
                {normalizedGroups.length === 1 ? '' : 's'}
              </span>
              <button
                type="button"
                onClick={handleLoadMore}
                disabled={loadingMoreGroups}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg shadow-sm bg-brand hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {loadingMoreGroups ? (
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-white/60 border-t-white rounded-full" />
                ) : null}
                <span>Load more</span>
              </button>
            </div>
          </div>
        ) : null
      ) : (
        <div className="px-4 sm:px-6 py-4 border-t border-gray-100 bg-white">
          <div className="flex items-center justify-end gap-4">
            {/* <span className="text-sm text-gray-500">
              Showing {visibleGroups.length} of {normalizedGroups.length} group
              {normalizedGroups.length === 1 ? '' : 's'}
            </span> */}
            {totalLocalPages > 1 ? (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={handlePrevPage}
                  disabled={!canPrev}
                  className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Previous page"
                >
                  <HiChevronLeft className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-1">
                  {getPaginationRange().map((page, idx) =>
                    page === 'dots' ? (
                      <span
                        // eslint-disable-next-line react/no-array-index-key
                        key={`dots-${idx}`}
                        className="px-2 text-sm text-gray-500"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        type="button"
                        onClick={() => setGroupsPage(page)}
                        className={`min-w-[30px] h-8 px-2 rounded-lg text-sm font-medium transition-colors ${
                          page === groupsPage
                            ? 'bg-brand border border-brand text-white shadow-sm hover:opacity-90'
                            : 'text-gray-900 hover:bg-gray-100'
                        }`}
                        aria-current={page === groupsPage ? 'page' : undefined}
                      >
                        {page}
                      </button>
                    )
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleNextPage}
                  disabled={!canNext}
                  className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Next page"
                >
                  <HiChevronRight className="w-4 h-4" />
                </button>
              </div>
            ) : null}
          </div>
        </div>
      )}
      </div>

      {activeGroup ? (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setActiveGroupKey(null)}
        >
          <div
            className="w-full max-w-4xl bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 sm:px-5 py-3 border-b border-gray-100 flex items-start justify-between gap-3">
              <div className="min-w-0 space-y-1">
                <div className="flex items-center  gap-2">
                  {/* <span className="text-sm text-gray-500">Group</span> */}
                  <span className="inline-flex items-center gap-2 px-2 py-2 text-[11px] rounded-lg bg-gray-100 text-gray-700 font-semibold shrink-0">
                    {activeGroup.country} · Group {activeGroup.group_index + 1}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {/* <span className="text-xs text-gray-500 whitespace-nowrap">
                    {activeUsers.length} user
                    {activeUsers.length === 1 ? '' : 's'}
                  </span> */}
                  {modalError ? (
                    <span className="text-[11px] text-red-600">{modalError}</span>
                  ) : null}
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-600">
                <div className="relative group mt-0 -ml-1">
                  <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-100 text-gray-500 text-[11px]">
                    i
                  </span>
                  <div className="pointer-events-none absolute left-1/2 -translate-x-[80%] top-full mt-1 whitespace-nowrap rounded-md bg-black text-white text-xs px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    {modalWeekMode === 'previous'
                      ? 'Toggle to view current week data for this group'
                      : 'Toggle to view previous week data for this group'}
                  </div>
                </div>

                <button
                  type="button"
                  role="switch"
                  aria-checked={modalWeekMode === 'previous'}
                  disabled={!canShowPreviousWeek || modalLoading}
                  onClick={() =>
                    handleModalWeekChange(
                      modalWeekMode === 'previous' ? 'current' : 'previous'
                    )
                  }
                  className={`relative inline-flex h-6 w-12 items-center rounded-full border transition-colors ${
                    modalWeekMode === 'previous'
                      ? 'bg-brand border-brand'
                      : 'bg-gray-200 border-gray-300'
                  } ${
                    !canShowPreviousWeek || modalLoading
                      ? 'opacity-50 cursor-not-allowed'
                      : 'cursor-pointer'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${
                      modalWeekMode === 'previous' ? 'translate-x-6' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="relative max-h-[70vh] overflow-x-auto overflow-y-scroll [scrollbar-gutter:stable]">
              {modalLoading ? (
                <div
                  className="absolute inset-0 z-20 flex items-center justify-center bg-white"
                  role="status"
                  aria-live="polite"
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-block w-8 h-8 border-4 border-gray-300 border-t-brand rounded-full animate-spin" />
                    {/* <span className="text-sm font-semibold text-gray-700">
                      Loading…
                    </span> */}
                  </div>
                </div>
              ) : null}
              <table className="w-full md:table-fixed table-auto border-collapse min-w-[720px]">
                <colgroup className="hidden md:table-column-group">
                  <col className="w-[14%]" />
                  <col className="w-[24%]" />
                  <col className="w-[18%]" />
                  <col className="w-[14%]" />
                </colgroup>
                <thead className="bg-white border-b border-[#ECECEC] sticky top-0">
                  <tr>
                    <th
                      scope="col"
                      className="px-3 md:px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider"
                    >
                      Rank
                    </th>
                    <th
                      scope="col"
                      className="px-6 md:px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider"
                    >
                      Profile Picture
                    </th>
                    <th
                      scope="col"
                      className="px-8 md:px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider"
                    >
                      Username
                    </th>
                    <th
                      scope="col"
                      className="px-3 md:px-4  py-3 text-center text-sm font-semibold text-gray-500 uppercase tracking-wider"
                    >
                      Points
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-[#ECECEC]">
                  {activeUsers.map((user, idx) => {
                    const r = Number(user?.rank ?? idx + 1);
                    const colors = rankColors(r);
                    const points = Number(
                      user?.points ?? user?.point ?? user?.score ?? 0
                    );
                    const displayName = user?.name || user?.username || 'N/A';
                    const displayUsername = user?.username
                      ? `@${user.username}`
                      : '';

                    const rowBgClass =
                      colors?.row ? colors.row : 'bg-white';

                    return (
                      <tr
                        key={user?.id || user?.username || `user-${idx}`}
                        className={`${rowBgClass} hover:bg-white/30 transition-colors`}
                      >
                        <td className="px-3 md:px-4 py-2.5 align-middle whitespace-nowrap">
                          <span
                            className={`inline-flex items-center justify-center min-w-9 h-7 px-2 rounded-md border text-xs font-semibold ${
                              colors?.badge
                                ? colors.badge
                                : 'border-gray-200 bg-gray-50 text-gray-800'
                            }`}
                          >
                            {r || '--'}
                          </span>
                        </td>
                        <td className="px-6 md:px-4 py-2.5 align-middle whitespace-nowrap">
                          <div className="flex items-center">
                            <Avatar
                              src={
                                user?.profile_picture ||
                                user?.profilePicture ||
                                null
                              }
                              name={user?.name}
                              username={user?.username}
                            />
                          </div>
                        </td>
                        <td className="px-7 md:px-4 py-2.5 align-middle min-w-0">
                          <div className="min-w-0">
                            <div
                              className="text-sm font-semibold truncate text-gray-900"
                              title={displayName}
                            >
                              {displayName}
                            </div>
                            <div
                              className="text-xs text-gray-500 truncate"
                              title={displayUsername || ''}
                            >
                              {displayUsername || '-'}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 md:px-4 py-2.5 text-center  align-middle whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-900 tabular-nums">
                            {Number.isFinite(points)
                              ? points.toLocaleString()
                              : '--'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-4 sm:px-5 py-3 border-t border-gray-100 flex justify-end">
              <button
                type="button"
                onClick={() => setActiveGroupKey(null)}
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default LeaderboardGroupedTable;

